import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import Head from 'components/head'

import getConfig from 'next/config'
const {
  publicRuntimeConfig: { defaultDescription, defaultCategoryBanner },
} = getConfig()

import { withDispatch } from 'store'
import { getContent } from 'store/article'
import { getCategoryBySlug, useCategory } from 'store/category'
import ContentLayout from 'components/layout/content'
import CategoryListing from 'components/layout/listing/category'
import Container, { LandingPagePanel } from 'components/container'

import { toInt } from 'util/conversion'
import { getImage } from 'util/image'

import { event, set } from 'util/analytics'

const CategoryHelmet = (category) => {
  const image = getImage([category.image], defaultCategoryBanner)
  return (
    <Head>
      <title>{category.name}</title>
      <meta
        name="description"
        content={category.description || category.name || defaultDescription}
      />
      <meta property="og:title" content={category.name} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        property="og:description"
        content={category.description || category.name || defaultDescription}
      />
      <meta property="og:image" content={image} />
      <link
        rel="alternate"
        type="application/rss+xml"
        title="RSS Feed"
        href={`/feed/${category.slug}.rss`}
      />
      <link
        rel="alternate"
        type="application/atom+xml"
        title="ATOM Feed"
        href={`/feed/${category.slug}.atom`}
      />
      <link
        rel="alternate"
        type="application/feed+json"
        title="JSON Feed"
        href={`/feed/${category.slug}.json`}
      />
    </Head>
  )
}

const CategoryIndex = ({ id, ...rest }) => {
  const category = useCategory(id)
  useEffect(() => {
    set({
      category: category.name,
    })
    event({
      category: 'category',
      action: 'list',
      label: `/${category.slug}`,
    })
  }, [category])

  if (category.landingPage) {
    return (
      <>
        {CategoryHelmet(category)}
        <Container>
          <ContentLayout
            id={category.landingPage}
            categoryId={category.id}
            WrapComponent={LandingPagePanel}
          />
        </Container>
      </>
    )
  }
  return (
    <>
      {CategoryHelmet(category)}
      <Container>
        <CategoryListing id={category.id} {...rest} />
      </Container>
    </>
  )
}
CategoryIndex.propTypes = {
  id: PropTypes.string.isRequired,
}
CategoryIndex.getInitialProps = async (context) => {
  const {
    query,
    store,
    fetchArticleContent,
    fetchMissing,
    fetchCategoriesBySlug,
    setCategoryHero,
    setArticleHero,
  } = context

  let category = getCategoryBySlug(store, query.category)
  if (!category) {
    const [found, ...rest] = await fetchCategoriesBySlug([query.category]) // eslint-disable-line no-unused-vars
    category = found
    if (!category) {
      return {
        error: 404,
      }
    }
  }
  let queries = [
    CategoryListing.getInitialProps({
      store,
      id: category.id,
      page: query.page,
      year: query.year,
      month: query.month,
    }),
  ]
  if (category.landingPage) {
    const content = getContent(store, category.landingPage)
    if (!content) {
      const getLandingPage = async (id) => {
        const [res, item] = await fetchArticleContent(id) // eslint-disable-line no-unused-vars
        await fetchMissing([item])
      }
      queries.push(getLandingPage(category.landingPage))
    }
  }

  const [listingProps = {}] = await Promise.all(queries)
  if (category.landingPage) {
    setArticleHero(category.landingPage, { subHeading: category.name })
  } else {
    setCategoryHero(category.id)
  }

  return {
    ...listingProps,
    query: {
      page: toInt(query.page, 1),
      year: toInt(query.year, null),
      month: toInt(query.month, null),
      category: query.category,
      ...(listingProps.query || {}),
    },
    id: category.id,
  }
}
export default withDispatch({
  article: ['fetchArticleContent'],
  category: ['fetchCategoriesBySlug'],
  shared: ['fetchMissing'],
  hero: ['setCategoryHero', 'setArticleHero'],
})(CategoryIndex)
